import { FC, ReactNode } from 'react';
import { ShieldTickIcon } from '../../../assets/icons/ShieldTickIcon';
import { parseText } from '../../../utils/string/parseText';
import { LocaleData } from '../../../models/locale/locale-data';

export const styles = {
  inner: 'w-11/12 md:max-w-[75%] mx-auto grid grid-cols-12  *:col-span-12',
  // ── Beko-live heading parity ──────────────────────────────────────────────
  // The live Beko site (a separate b2bce app) styles its headings as bold (700)
  // at fixed sizes, not via the design-system text tokens. The `beko-heading-*`
  // classes below are plain CSS classes defined in src/assets/styles.css and
  // reproduce live's measured values (same at mobile & desktop). NB: arbitrary
  // Tailwind values (text-[24px]) AND @utility classes are dropped by this
  // project's dev-mode build (vite tailwindcss optimize:false), so plain author
  // CSS classes are used. TO ALIGN BEKO WITH THE HAIER-GROUP TYPE SCALE INSTEAD:
  // delete the Beko-parity block in styles.css and revert these to the design-
  // system tokens — banner.title→`text-body-large`, hero.subtitle→`text-h1
  // font-bold`, registration.title→`text-h2`, hero.number→
  // `text-display-jumbotron`, and the BenefitsSection h3→`text-h2`.
  banner: {
    container: 'col-span-12 ticker bg-(--color-banner-bg-ticker)',
    title:
      'headline my-space-200 text-center text-(--color-banner-text-ticker) beko-heading-ticker',
  },
  hero: {
    container:
      'col-span-12 flex justify-center items-center gap-spacing-400 text-center bg-(--color-banner-bg-hero)',
    number: 'beko-heading-hero-number text-(--color-banner-text-hero)',
    subtitle: 'beko-heading-hero w-full text-(--color-banner-text-hero)',
  },
  cta: {
    container: 'col-span-12 flex justify-center cta-container',
    layout: 'text-center flex flex-col items-center py-space-600 gap-space-500 ',
  },
  registration: {
    title: 'beko-heading-cta headline text-center',
  },
  benefits: {
    // Fixed-basis flex items so the row centres (see BenefitsSection); width
    // roughly matches the old md:col-span-3 quarter-column.
    benefit: 'w-full md:w-1/4 flex flex-col items-center px-space-600 mb-space-500 md:mb-0',
  },
  asteriskContent: {
    container:
      'col-span-12 flex flex-col gap-space-500 text-center  border-t border-solid border-neutral-200',
    text: 'body-text-small mb-space-200',
  },
};

export const BannerSection: FC<{ text: string }> = ({ text }) => (
  <article className={styles.banner.container}>
    <div className={styles.inner}>
      <p className={styles.banner.title}>{text}</p>
    </div>
  </article>
);

export const HeroSection: FC<{ number: string; copy: string }> = ({ number, copy }) => (
  <article className={styles.hero.container}>
    <div className={styles.inner}>
      <div className="col-span-12 flex flex-col items-center gap-space-300 py-space-200">
        <p className={styles.hero.number}>{number}</p>
        <h1 className={styles.hero.subtitle}>{copy}</h1>
      </div>
    </div>
  </article>
);

export const PromoSection: FC<{ subCopy1: string; subCopy2: string }> = ({
  subCopy1,
  subCopy2,
}) => (
  <article className="bg-(--color-banner-bg-promotion) col-span-12">
    <div className={styles.inner}>
      {/* Beko-live parity: promo line 1 bold 22px, line 2 bold 16px→18px at
          ≥640px (live values, via the beko-promo-line-* classes in styles.css).
          To align Beko with the Haier-group type scale, drop those classes and
          let the text inherit the DS body size. */}
      <p className="col-span-12 text-center text-balance pt-space-300 mb-space-300 px-space-500 text-(--color-banner-text-promotion)">
        <span className="block beko-promo-line-1">{parseText(subCopy1)}</span>
        <span className="block beko-promo-line-2">{subCopy2}</span>
      </p>
    </div>
  </article>
);

/** Wraps a variant's CTA content in the shared CTA layout with its heading. */
export const CtaSection: FC<{ title: string; children: ReactNode }> = ({ title, children }) => (
  <article className={styles.cta.container}>
    <div className={styles.inner}>
      <div className={styles.cta.layout}>
        <h2 className={styles.registration.title}>{title}</h2>
        {children}
      </div>
    </div>
  </article>
);

export const BenefitsSection: FC<{ header: string; benefits: string[] }> = ({
  header,
  benefits,
}) => (
  <article className="col-span-12 bg-neutral py-space-700 px-space-400">
    <div className={styles.inner}>
      {/* Beko-live parity: benefits heading bold 30px (live, via beko-heading-
          benefits in styles.css). To align Beko with the Haier-group type scale,
          revert to `text-h2`. */}
      <h3 className="col-span-12 beko-heading-benefits py-space-600 text-center">{header}</h3>
      {/* Centred flex row (matches Beko production) so the benefits sit centred
          rather than left-aligned across a 12-col grid when they don't fill it. */}
      <div className="col-span-12 flex flex-col md:flex-row md:flex-wrap justify-center">
        {benefits.map((benefit, index) => (
          <div key={index} className={styles.benefits.benefit}>
            <span className="text-(--color-icon-list-decorative)">
              <ShieldTickIcon />
            </span>
            <p className="mt-space-200 text-center">{benefit}</p>
          </div>
        ))}
      </div>
    </div>
  </article>
);

export const SmallPrintSection: FC<{ data: LocaleData }> = ({ data }) => (
  <article className={styles.asteriskContent.container}>
    <div className={`${styles.inner} my-space-700`}>
      <p className={styles.asteriskContent.text}>
        {parseText(data?.heroAsterix?.[0] ?? '', data?.heroAsterixLinks ?? [])}
      </p>
      <p className={styles.asteriskContent.text}>
        {parseText(data?.phoneNumberAsterix?.[0] ?? '', data?.phoneNumberAsterixLinks ?? [])}
      </p>
    </div>
  </article>
);
